/**
 * IXVerify v2 — typed API layer (ixv2 WP-2.3).
 *
 * Every call rides the shared axios instance (`services/api`) so the platform
 * seams apply unchanged (R6): Supabase JWT, `X-Site-Id`, `X-User-Id` headers
 * from the request interceptor; 401-refresh + maintenance handling from the
 * response interceptor. All routes are under `/api/ixverify` behind
 * `requireIxvEntitlement` (R1) — the frontend adds no new HTTP clients and no
 * ungated calls.
 *
 * Backend reality this UI is built against (see
 * docs/ixverify-v2-evidence/wp-2-1-roster-service.md):
 *   - the 5 subject routes are LIVE (WP-2.1);
 *   - the studies + profile routes are 501 stubs until WP-3.1/WP-5.1 — the
 *     pages degrade to a clean "not yet available" state via `is501()`.
 */
import api from '../../services/api';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

/** `ixv_subjects.source` — provenance of a roster row. */
export type SubjectSource = 'crio' | 'manual' | 'csv';

/** Free-text study status; common values used for the filter chips. */
export const SUBJECT_STATUSES = ['screening', 'enrolled', 'discontinued', 'completed'] as const;

export interface IxvSubject {
  id: string;
  site_study_id: string;
  subject_number: string;
  initials: string | null;
  /** ISO date, decrypted server-side (the read is audited server-side).
   *  Never shown raw: people read it through lib/dob `formatDob` (01Dec2020). */
  dob: string | null;
  /** Ciphertext present but undecryptable (key rotation/corruption containment). */
  dob_unreadable?: boolean;
  status: string | null;
  source: SubjectSource;
  /** §3.4: true for any non-CRIO row without an admin DOB confirmation. */
  pending_confirmation: boolean;
  crio_patient_id?: string | null;
  crio_subject_external_id?: string | null;
  created_at?: string;
  updated_at?: string;
}

export interface SubjectListResult {
  subjects: IxvSubject[];
  limit: number;
  offset: number;
}

export interface ImportPreview {
  total_rows: number;
  to_create: Array<{ row: number; subject_number: string }>;
  /** `changes` is field NAMES only — stored values never ride a preview (R3). */
  to_update: Array<{ row: number; id: string; subject_number: string; changes: string[] }>;
  unchanged: number;
  errors: Array<{ row: number; error: string }>;
}

export interface ImportCommitResult {
  created: number;
  updated: number;
  unchanged: number;
  errors: Array<{ row: number; error: string }>;
}

/**
 * Study shape for the WP-3.1 `GET /api/ixverify/studies` surface (Appendix A:
 * site_studies CRUD + crio_study_id + registry link + profile status). The
 * route is a 501 stub today, so every field beyond `id`/`protocol_id` is
 * optional — the page renders defensively against whatever WP-3.1 returns.
 */
export interface IxvStudy {
  id: string;
  protocol_id: string;
  protocol_name?: string | null;
  sponsor_name?: string | null;
  is_active?: boolean;
  /** Pilot r2 admin archive: non-null = hidden from the default list and
   *  every study picker/filter; History labels still render from the row. */
  archived_at?: string | null;
  archived_by?: string | null;
  /** §3.4 — ties the protocol to its CRIO study for subject auto-sync. */
  crio_study_id?: string | null;
  /** Registry link (eReg-first): the eReg study this row is connected to. */
  registry_protocol_id?: string | null;
  registry_protocol?: { id?: string; protocol_number?: string | null; study_nickname?: string | null } | null;
  /** Exact-match registry candidate for unlinked rows — one-click link. */
  registry_suggestion?: { id: string; protocol_number?: string | null; study_nickname?: string | null } | null;
  /** ixv_study_profiles summary, when the WP-3.1 payload includes it. */
  profile?: IxvStudyProfileSummary | null;
}

/** Pilot r4: the study reading legend — model-written, human-truth-anchored
 *  layout knowledge (stable landmarks + distractors) for this study's docs. */
export interface IxvReadingLegend {
  document_guide?: string;
  kit_number_guide?: string;
  label_guide?: string | null;
  distractors?: string[];
  confidence?: 'high' | 'medium' | 'low';
  generated_at?: string;
}

export interface IxvStudyProfileSummary {
  revision?: number;
  exemplar_count?: number;
  kit_number_pattern?: string | null;
  typical_kit_count?: number | null;
  high_scrutiny?: boolean;
  updated_at?: string | null;
  /** Rolling counts, when reported. */
  session_count?: number;
  /** §5.4 state (WP-5.1): teaching until enough confirmed sessions. */
  state?: 'teaching' | 'learned';
  confirmed_sessions?: number;
  /** Pilot r4: the study reading legend, when one has been generated. */
  reading_legend?: IxvReadingLegend | null;
}

// ---------------------------------------------------------------------------
// Error helpers
// ---------------------------------------------------------------------------

/** True when the backend answered 501 (`IXV_NOT_IMPLEMENTED`) — the route is a
 *  v2 stub whose real handler lands in a later WP. Pages show the shared
 *  "not yet available" state instead of an error. */
export function is501(err: unknown): boolean {
  return (err as any)?.response?.status === 501;
}

/** 503 `IXV_ENCRYPTION_UNAVAILABLE` — `IXV_ENCRYPTION_KEY` not provisioned yet
 *  (R10: ships out-of-band before launch). Surface as "temporarily
 *  unavailable", never an error-toast loop. */
export function isEncryptionUnavailable(err: unknown): boolean {
  const resp = (err as any)?.response;
  return resp?.status === 503 && resp?.data?.code === 'IXV_ENCRYPTION_UNAVAILABLE';
}

/** Best-effort server error message with a quiet fallback. */
export function errorMessage(err: unknown, fallback: string): string {
  const data = (err as any)?.response?.data;
  return (typeof data?.error === 'string' && data.error) || fallback;
}

// ---------------------------------------------------------------------------
// Subjects (LIVE — WP-2.1)
// ---------------------------------------------------------------------------

export async function listSubjects(params: {
  study?: string;
  search?: string;
  status?: string;
  limit?: number;
  offset?: number;
}): Promise<SubjectListResult> {
  const query: Record<string, string | number> = {};
  if (params.study) query.study = params.study;
  if (params.search) query.search = params.search;
  if (params.status) query.status = params.status;
  if (params.limit != null) query.limit = params.limit;
  if (params.offset != null) query.offset = params.offset;
  const { data } = await api.get('/api/ixverify/subjects', { params: query });
  return data;
}

export async function createSubject(body: {
  site_study_id: string;
  subject_number: string;
  initials?: string;
  dob?: string;
  status?: string;
}): Promise<IxvSubject> {
  const { data } = await api.post('/api/ixverify/subjects', body);
  return data.subject;
}

export async function updateSubject(
  id: string,
  body: {
    subject_number?: string;
    initials?: string;
    dob?: string;
    status?: string;
    confirm?: true;
  }
): Promise<IxvSubject> {
  const { data } = await api.patch(`/api/ixverify/subjects/${id}`, body);
  return data.subject;
}

/** Confirm a manual/CSV row's DOB entry (§3.4). The server stamps
 *  `confirmed_by` — the UI never auto-confirms (R5). */
export function confirmSubject(id: string): Promise<IxvSubject> {
  return updateSubject(id, { confirm: true });
}

export async function importSubjectsPreview(site_study_id: string, csv: string): Promise<ImportPreview> {
  const { data } = await api.post('/api/ixverify/subjects/import', {
    site_study_id,
    csv,
    mode: 'preview',
  });
  return data;
}

export async function importSubjectsCommit(site_study_id: string, csv: string): Promise<ImportCommitResult> {
  const { data } = await api.post('/api/ixverify/subjects/import', {
    site_study_id,
    csv,
    mode: 'commit',
  });
  return data;
}

// ---------------------------------------------------------------------------
// Studies + learning profiles (501 STUBS until WP-3.1 / WP-5.1 — callers
// must handle is501 and degrade gracefully)
// ---------------------------------------------------------------------------

export async function listStudies(): Promise<IxvStudy[]> {
  const { data } = await api.get('/api/ixverify/studies');
  // Tolerate either a bare array or {studies:[...]} from the future handler.
  return Array.isArray(data) ? data : data?.studies ?? [];
}

/** eReg-first (2026-08-12): a study is ADDED by referencing an existing eReg
 *  registry study — never by typing free-text identity. The backend copies
 *  protocol number/name/sponsor from the registry and heals an unlinked local
 *  row with the same protocol string instead of duplicating it. */
export async function createStudy(body: {
  registry_protocol_id: string;
  crio_study_id?: string | null;
}): Promise<IxvStudy> {
  const { data } = await api.post('/api/ixverify/studies', body);
  return data?.study ?? data;
}

/** Admin: connect an existing (usually auto-created) study to its eReg record. */
export async function linkStudyRegistry(id: string, registryProtocolId: string): Promise<IxvStudy> {
  const { data } = await api.post(`/api/ixverify/studies/${id}/registry-link`, {
    registry_protocol_id: registryProtocolId,
  });
  return data?.study ?? data;
}

/** Admin: wrong link chosen — detach the study from the registry. */
export async function unlinkStudyRegistry(id: string): Promise<IxvStudy> {
  const { data } = await api.delete(`/api/ixverify/studies/${id}/registry-link`);
  return data?.study ?? data;
}

export async function updateStudy(
  id: string,
  body: { protocol_name?: string; sponsor_name?: string; is_active?: boolean; crio_study_id?: string | null }
): Promise<IxvStudy> {
  const { data } = await api.patch(`/api/ixverify/studies/${id}`, body);
  return data?.study ?? data;
}

/** Pilot r2 — what a hard delete would destroy, or why it is blocked. */
export interface StudyDeletePreview {
  eligible: boolean;
  signed_sessions: number;
  signed_assets: number;
  counts: { sessions: number; subjects: number; assets: number };
}

/** Admin: preview the delete impact (counts for the typed-confirmation dialog). */
export async function getStudyDeletePreview(id: string): Promise<StudyDeletePreview> {
  const { data } = await api.get(`/api/ixverify/studies/${id}/delete-preview`);
  return data;
}

/** Admin: HARD delete a study with no signed records — sessions, subjects,
 *  and stored photos are destroyed for real. A study holding signed records
 *  answers 409 `IXV_STUDY_HAS_SIGNED_RECORDS` (offer Archive instead). */
export async function deleteStudy(id: string): Promise<{ deleted: boolean; counts: StudyDeletePreview['counts'] }> {
  const { data } = await api.delete(`/api/ixverify/studies/${id}`);
  return data;
}

/** Admin: archive (reversible — drops the study from lists and pickers). */
export async function archiveStudy(id: string): Promise<IxvStudy> {
  const { data } = await api.post(`/api/ixverify/studies/${id}/archive`, {});
  return data?.study ?? data;
}

/** Admin: bring an archived study back. */
export async function unarchiveStudy(id: string): Promise<IxvStudy> {
  const { data } = await api.post(`/api/ixverify/studies/${id}/unarchive`, {});
  return data?.study ?? data;
}

export async function getStudyProfile(studyId: string): Promise<unknown> {
  const { data } = await api.get(`/api/ixverify/studies/${studyId}/profile`);
  return data;
}

/** Reset the learning profile (re-teach) — §5.4; v1's only recovery, kept as
 *  an explicit admin action. */
export async function resetStudyProfile(studyId: string): Promise<void> {
  await api.delete(`/api/ixverify/studies/${studyId}/profile`);
}

// ---------------------------------------------------------------------------
// Sessions (LIVE — WP-3.1; dashboard/history surfaces WP-3.2/WP-3.3). Route
// shapes are pinned by docs/ixverify-v2-evidence/wp-3-1-session-api.md —
// build against those exact payloads; do not guess.
// ---------------------------------------------------------------------------

export type SessionStatus =
  | 'draft'
  | 'staged'
  | 'capturing'
  | 'review'
  | 'completed'
  | 'second_check_pending'
  | 'verified'
  | 'mismatch_recorded'
  | 'cancelled';

export const SESSION_STATUSES: SessionStatus[] = [
  'draft', 'staged', 'capturing', 'review', 'completed',
  'second_check_pending', 'verified', 'mismatch_recorded', 'cancelled',
];

/** The PHI-free list/SSE projection (`sessionSummary` server-side — no DOB). */
export interface IxvSessionSummary {
  id: string;
  site_id: string;
  site_study_id: string | null;
  subject_id: string | null;
  created_by: string;
  status: SessionStatus;
  staged_at: string | null;
  staged_source: string | null;
  verification_code: string | null;
  visit_name: string | null;
  dispense_date: string | null;
  mismatch: boolean | null;
  dob_attested_at: string | null;
  dob_attested_by: string | null;
  step_timestamps: Record<string, any> | null;
  completed_at: string | null;
  created_at: string;
  updated_at: string;
}

/** One mismatch record + its (optional) reopen — pilot r3 provenance.
 *  Lives on the session row (detail payload only), rendered on the mismatch
 *  surface and disclosed on any report published after a reopen. */
export interface MismatchRecord {
  recorded_at?: string;
  recorded_by?: string | null;
  reason?: string | null;
  counts?: Partial<Record<KitMatchStatus, number>> | null;
  reopened_at?: string;
  reopened_by?: string | null;
  reopen_reason?: string | null;
}

/** Full row — the summary plus the review substrate. */
export interface IxvSession extends IxvSessionSummary {
  extraction?: IxvExtraction | null;
  assignment?: {
    fields?: Record<string, string | null>;
    kits?: Array<{ kit_number: string; description?: string | null }>;
    confirmed_by?: string;
    confirmed_at?: string;
    attestation?: { claims: string[]; attested_by: string; attested_at: string; copy_certified: boolean };
  } | null;
  overrides?: Array<{ field: string; reason: string | null }> | null;
  mismatch_records?: MismatchRecord[] | null;
}

export type FieldState = 'green' | 'amber';

export interface ExtractedField {
  value: string | null;
  confidence: 'high' | 'medium' | 'low';
  evidence: string | null;
  state: FieldState;
  reasons: string[];
}

export interface ExtractedKit {
  kit_number: { value: string | null; confidence: string; evidence: string | null };
  description: string | null;
  state: FieldState;
  reasons: string[];
  canAutoMatch: boolean;
}

export type CaptureVerdict = 'ok' | 'retake' | 'add_page';

/** A recorded use-anyway override of the R12 capture advisory (pilot r1).
 *  Stamped server-side into `extraction.capture.override`; a re-extract
 *  replaces the payload wholesale, so a fresh verdict clears it. */
export interface CaptureOverride {
  verdict: CaptureVerdict;
  reasons: string[];
  overridden_by: string;
  overridden_at: string;
}

/** `ixv_sessions.extraction` — the persisted review payload (WP-3.1). */
export interface IxvExtraction {
  document_kind: string;
  fields: {
    protocol_id: ExtractedField;
    subject_number: ExtractedField;
    visit_name: ExtractedField;
    dispense_date: ExtractedField;
  };
  kits: ExtractedKit[];
  not_kit_numbers_seen: string[];
  agreement: { mode?: string; agreed?: boolean; disagreements?: string[] } | null;
  validators: Array<{ name: string; field: string; ok: boolean; detail: string | null }>;
  capture: { verdict: CaptureVerdict; reasons: string[]; check: Record<string, any> | null; override?: CaptureOverride | null } | null;
  review_required: boolean;
  asset_id?: string | null;
  extracted_at?: string;
  /** WP-5.1: §5.4 teaching-banner flag, persisted with the review payload. */
  teaching_mode?: boolean;
}

export type KitMatchStatus = 'pending' | 'matched' | 'mismatch' | 'missing' | 'extra' | 'manual_confirmed';

export interface IxvKit {
  id: string;
  session_id: string;
  assigned_kit_number: string | null;
  scanned_kit_number: string | null;
  scan_method: 'barcode' | 'vision' | 'manual' | null;
  barcode_raw: string | null;
  asset_id: string | null;
  manual_reason: string | null;
  match_status: KitMatchStatus;
  compared_at: string | null;
  /** Confusable-character annotation (both readings; human tap required — R5). */
  ambiguity: {
    assigned_reading: string;
    scanned_reading: string;
    confusable_classes: string[];
  } | null;
}

export interface KitState {
  assigned_count: number;
  counts: Record<KitMatchStatus, number>;
  all_resolved: boolean;
  pass: boolean;
}

export type AssetKind = 'ixrs_document' | 'ixrs_photo' | 'ixrs_text' | 'label_photo' | 'report_pdf';

export interface IxvAsset {
  id: string;
  session_id: string;
  kind: AssetKind;
  sha256: string | null;
  tier: 'original' | 'rendition';
  rendition_of: string | null;
  capture_check: Record<string, any> | null;
  page_index: number | null;
  content_type: string | null;
  size_bytes: number | null;
  page_count: number | null;
  source: string | null;
  created_at: string;
  /** Discard mark (pilot r1): non-null = no longer evidence (excluded from
   *  extraction, attestation gates, and the report). */
  discarded_at?: string | null;
  discarded_by?: string | null;
  /** Hard-delete tombstone (pilot r1.1, owner decision 2026-08-21): non-null
   *  = the stored bytes were removed for real (the page was never covered by
   *  a signature when discarded). Signed-over pages never carry this. */
  deleted_at?: string | null;
  deleted_by?: string | null;
  /** Signature-coverage stamp: set when any attestation was applied while
   *  this asset was live; such assets are retained forever (soft discard). */
  attested_first_at?: string | null;
}

/** GET /sessions/:id — the composite-review payload. */
export interface SessionDetail {
  session: IxvSession;
  kits: IxvKit[];
  kit_state: KitState;
  assets: IxvAsset[];
  subject: IxvSubject | null;
  /** The session's second check, when one exists (WP-4.1 — a rejected row is
   *  the admin-review flag). */
  second_check?: IxvSecondCheckRow | null;
}

export interface SessionListResult {
  sessions: IxvSessionSummary[];
  limit: number;
  offset: number;
}

export async function createSession(body?: { site_study_id?: string; visit_name?: string }): Promise<IxvSession> {
  const { data } = await api.post('/api/ixverify/sessions', body || {});
  return data.session;
}

/** Stage an assignment ahead of the visit (§3.2 staged flow). One of
 *  `file`/`text` is required. Extraction is a separate, kill-switched call —
 *  callers chain `extractSession` right after (WP-3.1 decision 1). */
export async function createStagedSession(input: {
  file?: File | Blob;
  filename?: string;
  text?: string;
  site_study_id?: string;
  visit_name?: string;
}): Promise<{ session: IxvSession; asset: { id: string; kind: AssetKind; sha256: string; tier: string } | null }> {
  if (input.file) {
    const form = new FormData();
    form.append('file', input.file, input.filename || (input.file as File).name || 'assignment');
    if (input.site_study_id) form.append('site_study_id', input.site_study_id);
    if (input.visit_name) form.append('visit_name', input.visit_name);
    const { data } = await api.post('/api/ixverify/sessions/staged', form);
    return data;
  }
  const { data } = await api.post('/api/ixverify/sessions/staged', {
    text: input.text,
    site_study_id: input.site_study_id,
    visit_name: input.visit_name,
  });
  return data;
}

export async function listSessions(params: {
  /** History's status <select> passes free strings; sessions use the enum. */
  status?: SessionStatus | string;
  mine?: boolean;
  limit?: number;
  offset?: number;
} = {}): Promise<SessionListResult> {
  const query: Record<string, string | number> = {};
  if (params.status) query.status = params.status;
  if (params.mine) query.mine = 'true';
  if (params.limit != null) query.limit = params.limit;
  if (params.offset != null) query.offset = params.offset;
  const { data } = await api.get('/api/ixverify/sessions', { params: query });
  return data;
}

export async function getSession(id: string): Promise<SessionDetail> {
  const { data } = await api.get(`/api/ixverify/sessions/${id}`);
  return data;
}

/** Order-free multipart asset ingestion (two-tier, auto-classified — R12).
 *  `kind` hints from the capture mode; `link_asset_id` ties rendition↔original
 *  in whichever order they land. */
export async function uploadSessionAsset(
  sessionId: string,
  file: Blob,
  opts: {
    filename?: string;
    tier?: 'original' | 'rendition';
    kind?: AssetKind;
    source?: 'upload' | 'camera' | 'paste';
    page_index?: number;
    link_asset_id?: string;
  } = {}
): Promise<{ asset: { id: string; kind: AssetKind; tier: string; sha256: string; page_index: number | null }; session: IxvSessionSummary }> {
  const form = new FormData();
  form.append('file', file, opts.filename || (file as File).name || 'capture.jpg');
  if (opts.tier) form.append('tier', opts.tier);
  if (opts.kind) form.append('kind', opts.kind);
  if (opts.source) form.append('source', opts.source);
  if (opts.page_index != null) form.append('page_index', String(opts.page_index));
  if (opts.link_asset_id) form.append('link_asset_id', opts.link_asset_id);
  const { data } = await api.post(`/api/ixverify/sessions/${sessionId}/assets`, form);
  return data;
}

/** Pasted assignment text (hashed, retained evidence — R4). */
export async function addSessionText(sessionId: string, text: string): Promise<{ asset: { id: string }; session: IxvSessionSummary }> {
  const { data } = await api.post(`/api/ixverify/sessions/${sessionId}/assets`, { text });
  return data;
}

/** Discard one captured document page (pilot r1, hard/soft split in r1.1).
 *  The SERVER decides the mode from signature coverage — never this client:
 *  `mode: 'deleted'` = no signature ever covered the page, so the stored file
 *  (and its pair tier) was removed for real; `mode: 'discarded'` = the page
 *  was signed over at some point, so it is only marked removed (bytes
 *  retained). Either way the page leaves extraction/attestation/report, the
 *  action is audited, and a discard after attestation voids the attestation
 *  server-side. Use `mode` to phrase the confirmation honestly. */
export async function discardSessionAsset(
  sessionId: string,
  assetId: string
): Promise<{
  asset: IxvAsset;
  pair: IxvAsset | null;
  session: IxvSessionSummary;
  already_discarded: boolean;
  mode: 'deleted' | 'discarded';
}> {
  const { data } = await api.post(`/api/ixverify/sessions/${sessionId}/assets/${assetId}/discard`, {});
  return data;
}

/** Record a use-anyway override of the R12 capture advisory (pilot r1). The
 *  verdict is untouched; the CRC's explicit decision to proceed is stamped
 *  into the session, audited, and carried onto the report's attestation. */
export async function overrideCaptureCheck(
  sessionId: string
): Promise<{ session: IxvSessionSummary; override: CaptureOverride; already_overridden: boolean }> {
  const { data } = await api.post(`/api/ixverify/sessions/${sessionId}/capture-override`, {});
  return data;
}

export interface ExtractResult {
  session: IxvSessionSummary;
  extraction: IxvExtraction;
  subject: IxvSubject | null;
  subject_match: 'matched' | 'unknown' | 'no_study';
  /** WP-5.1: §5.4 teaching banner — true until the study reaches learned. */
  teaching_mode?: boolean;
}

/** Assignment extraction (AI spend — kill-switched, R2). */
export async function extractSession(sessionId: string, assetId?: string): Promise<ExtractResult> {
  const { data } = await api.post(`/api/ixverify/sessions/${sessionId}/extract`, assetId ? { asset_id: assetId } : {});
  return data;
}

export interface ConfirmResult {
  session: IxvSession;
  kits: IxvKit[];
  kit_state: KitState;
  subject_match: 'matched' | 'unknown';
  study: IxvStudy;
  study_created: boolean;
  /** WP-5.1: §5.4 teaching banner state after this confirm. */
  teaching_mode?: boolean;
}

/** Exception-based confirm: body values are the FINAL values; the server
 *  derives + records corrections against the AI proposal (the learning signal). */
export async function confirmSession(
  sessionId: string,
  body: {
    fields: { protocol_id?: string | null; subject_number?: string | null; visit_name?: string | null; dispense_date?: string | null };
    kits: Array<{ kit_number: string; description?: string | null }>;
    overrides?: Array<{ field: string; reason?: string | null }>;
  }
): Promise<ConfirmResult> {
  const { data } = await api.post(`/api/ixverify/sessions/${sessionId}/confirm`, body);
  return data;
}

/** Match an existing roster subject, or inline-add (§3.2: DOB keyed once,
 *  source='manual', pending admin confirmation). */
export async function attachSessionSubject(
  sessionId: string,
  body: { subject_id?: string } | { subject_number: string; dob?: string; initials?: string; status?: string }
): Promise<{ session: IxvSessionSummary; subject: IxvSubject; inline_added: boolean }> {
  const { data } = await api.post(`/api/ixverify/sessions/${sessionId}/subject`, body);
  return data;
}

export interface KitScanResult {
  session: IxvSessionSummary;
  kit?: IxvKit;
  scans?: IxvKit[];
  kits: IxvKit[];
  kit_state: KitState;
  duplicate?: boolean;
  suggestions?: Array<{ kit_id: string; assigned_kit_number: string; similarity: number }>;
  barcode?: { type: string; gtin?: string | null; lot?: string | null; expiry?: string | null; warnings?: string[]; candidates?: string[] } | null;
  vision?: { kit_numbers: string[]; not_kit_numbers_seen: string[] };
}

/** Kit from a client-decoded barcode payload (raw payload; the server owns GS1 parsing). */
export async function addKitFromBarcode(sessionId: string, barcode: string, replaceKitId?: string): Promise<KitScanResult> {
  const { data } = await api.post(`/api/ixverify/sessions/${sessionId}/kits`, {
    barcode,
    ...(replaceKitId ? { replace_kit_id: replaceKitId } : {}),
  });
  return data;
}

/** Kit from a label photo (vision read — kill-switched). Optional barcode for
 *  the both-sources-must-agree cross-check. */
export async function addKitFromPhoto(
  sessionId: string,
  photo: Blob,
  opts: { barcode?: string; replaceKitId?: string } = {}
): Promise<KitScanResult> {
  const form = new FormData();
  form.append('photo', photo, 'label.jpg');
  if (opts.barcode) form.append('barcode', opts.barcode);
  if (opts.replaceKitId) form.append('replace_kit_id', opts.replaceKitId);
  const { data } = await api.post(`/api/ixverify/sessions/${sessionId}/kits`, form);
  return data;
}

/** Manual kit entry — always possible, always needs a recorded reason (§3.2). */
export async function addKitManual(
  sessionId: string,
  kitNumber: string,
  reason: string,
  replaceKitId?: string
): Promise<KitScanResult> {
  const { data } = await api.post(`/api/ixverify/sessions/${sessionId}/kits`, {
    kit_number: kitNumber,
    method: 'manual',
    reason,
    ...(replaceKitId ? { replace_kit_id: replaceKitId } : {}),
  });
  return data;
}

export type KitDecision = 'confirm_match' | 'mark_missing' | 'remove';

/** A recorded human decision on a kit row (R5: anything non-exact needs one).
 *  The reason is required for confirm_match / mark_missing; optional for
 *  remove (pilot r4 — undoing an accidental scan is one tap, the decision
 *  itself is the record). */
export async function decideKit(
  sessionId: string,
  kitId: string,
  decision: KitDecision,
  reason?: string
): Promise<{ session: IxvSessionSummary; kits: IxvKit[]; kit_state: KitState }> {
  const { data } = await api.post(`/api/ixverify/sessions/${sessionId}/kits`, {
    kit_id: kitId,
    decision,
    ...(reason && reason.trim() ? { reason } : {}),
  });
  return data;
}

/** The combined attestation — one signature action, three recorded claims. */
export async function attestSession(
  sessionId: string,
  body: { kits_confirmed: boolean; dob_confirmed: boolean; copy_certified?: boolean }
): Promise<{ session: IxvSessionSummary; claims: string[]; kit_state: KitState }> {
  const { data } = await api.post(`/api/ixverify/sessions/${sessionId}/attest`, body);
  return data;
}

export interface PublishResult {
  session: IxvSession;
  report: { id: string; revision: number; sha256: string; page_count: number; verification_code: string };
  distribution: Array<{ to: string; channel: string; status: string; at?: string; note?: string }>;
}

/** Inline publish (R11): seal + verification code + in-request PDF + send hand-off. */
export async function completeSession(sessionId: string): Promise<PublishResult> {
  const { data } = await api.post(`/api/ixverify/sessions/${sessionId}/complete`);
  return data;
}

/** Record a mismatch — a CAUGHT ERROR (success, not failure). The one exit
 *  from the recorded state is the audited reopen below (pilot r3). */
export async function recordMismatch(sessionId: string, reason?: string): Promise<{ session: IxvSessionSummary; kit_state: KitState }> {
  const { data } = await api.post(`/api/ixverify/sessions/${sessionId}/mismatch`, reason ? { reason } : {});
  return data;
}

/** Reopen a recorded mismatch for correction (pilot r3): reason required,
 *  audited, admins notified; the record itself is preserved and disclosed on
 *  any report published later. The session returns to review/capturing. */
export async function reopenMismatch(sessionId: string, reason: string): Promise<{ session: IxvSessionSummary }> {
  const { data } = await api.post(`/api/ixverify/sessions/${sessionId}/reopen`, { reason });
  return data;
}

// A pristine (nothing entered, no photos) draft/staged session is deleted on
// cancel rather than kept as a cancelled session: session null, discarded true.
export async function cancelSession(sessionId: string, reason?: string): Promise<{ session: IxvSessionSummary | null; discarded?: boolean }> {
  const { data } = await api.post(`/api/ixverify/sessions/${sessionId}/cancel`, reason ? { reason } : {});
  return data;
}

/** Presign the latest report revision ON DEMAND (`pdf_url` is never stored —
 *  the v1 expiring-stored-URL defect, fixed).
 *
 *  `inline` signs for in-browser rendering rather than a forced download —
 *  what `ReportPreview` embeds. The download filename rides both dispositions,
 *  so the PDF viewer's own Save button still names the file correctly. */
export async function getReportUrl(
  sessionId: string,
  opts: { inline?: boolean } = {}
): Promise<{ url: string; revision: number; sha256: string }> {
  const { data } = await api.get(`/api/ixverify/sessions/${sessionId}/report-url`, {
    params: opts.inline ? { disposition: 'inline' } : undefined,
  });
  return data;
}

/** The sealed report's BYTES for the in-app viewer (2026-10-03), served by
 *  the api from its own origin so pdf.js can render EVERY page (an iframe
 *  embed drew only page one on phones, and a bucket URL is cross-origin to
 *  pdf.js). Revision, hash and page count ride exposed response headers.
 *  R3: the PDF carries the DOB — the Blob stays in component state. */
export async function getReportBlob(
  sessionId: string
): Promise<{ blob: Blob; revision: number | null; sha256: string | null; pageCount: number | null }> {
  const res = await api.get(`/api/ixverify/sessions/${sessionId}/report`, { responseType: 'blob' });
  const headers = (res.headers || {}) as Record<string, string | undefined>;
  const num = (v: string | undefined) => {
    const n = v != null ? parseInt(v, 10) : NaN;
    return Number.isFinite(n) ? n : null;
  };
  const data = res.data as Blob | ArrayBuffer | string;
  const blob =
    data instanceof Blob ? data : new Blob([data as ArrayBuffer | string], { type: 'application/pdf' });
  return {
    blob,
    revision: num(headers['x-ixv-report-revision']),
    sha256: headers['x-ixv-report-sha256'] || null,
    pageCount: num(headers['x-ixv-report-pages']),
  };
}

/** Queue a redistribution. Empty/omitted recipients = retry the report's
 *  failed deliveries; addresses must pass the R3 rule (site user / CRIO
 *  intake / admin allow-list) or the whole request is rejected. */
export async function resendReport(
  sessionId: string,
  recipients?: string[]
): Promise<{ queued: Array<{ to: string; channel: string; status: string }>; revision: number }> {
  const { data } = await api.post(`/api/ixverify/sessions/${sessionId}/resend`,
    recipients && recipients.length > 0 ? { recipients } : {});
  return data;
}

// ---------------------------------------------------------------------------
// Site settings + saved recipients (LIVE — WP-3.3)
// ---------------------------------------------------------------------------

export interface IxvSettings {
  site_id: string;
  crio_intake_email: string | null;
  /** Default OFF (plan §15 Q4 / decision D3). */
  require_second_check: boolean;
  external_recipient_allowlist: string[];
  daily_budget_cents?: number | null;
  updated_at?: string;
  updated_by?: string | null;
}

export async function getSettings(): Promise<IxvSettings> {
  const { data } = await api.get('/api/ixverify/settings');
  return data.settings;
}

export async function updateSettings(patch: {
  crio_intake_email?: string | null;
  require_second_check?: boolean;
  external_recipient_allowlist?: string[];
  daily_budget_cents?: number | null;
}): Promise<IxvSettings> {
  const { data } = await api.patch('/api/ixverify/settings', patch);
  return data.settings;
}

export interface IxvSavedRecipient {
  id: string;
  site_id: string;
  email: string;
  label: string | null;
  created_at?: string;
}

export async function listRecipients(): Promise<IxvSavedRecipient[]> {
  const { data } = await api.get('/api/ixverify/recipients');
  return data.recipients ?? [];
}

export async function addRecipient(body: { email: string; label?: string }): Promise<IxvSavedRecipient> {
  const { data } = await api.post('/api/ixverify/recipients', body);
  return data.recipient;
}

export async function deleteRecipient(id: string): Promise<void> {
  await api.delete(`/api/ixverify/recipients/${id}`);
}

/** 403 IXV_RECIPIENT_NOT_ALLOWED — the R3 distribution wall. */
export function isRecipientNotAllowed(err: unknown): boolean {
  const resp = (err as any)?.response;
  return resp?.status === 403 && resp?.data?.code === 'IXV_RECIPIENT_NOT_ALLOWED';
}

/** Rejected addresses from a 403 resend/save response, when reported. */
export function rejectedRecipients(err: unknown): Array<{ to: string; reason: string }> {
  return (err as any)?.response?.data?.rejected ?? [];
}

// ---------------------------------------------------------------------------
// Audit viewer (LIVE — WP-4.2). Admin-only server-side (403
// IXV_ADMIN_REQUIRED for anyone else); reads the LIVE data plane through the
// gated router — v1's viewer read the frozen snapshot. Rows are PHI-free by
// construction (R3: details carry identifiers/metrics only).
// ---------------------------------------------------------------------------

export interface IxvAuditActor {
  id: string;
  name: string | null;
  email: string | null;
}

export interface IxvAuditEntry {
  id: string;
  action: string;
  actor_id: string | null;
  actor: IxvAuditActor | null;
  site_id: string | null;
  target_type: string | null;
  target_id: string | null;
  details: Record<string, unknown>;
  ip_address: string | null;
  user_agent: string | null;
  created_at: string;
}

export interface AuditFilters {
  /** ISO date/datetime lower bound (inclusive). */
  from?: string;
  /** ISO date/datetime upper bound (a bare date covers the whole day). */
  to?: string;
  /** User id (uuid) or email address. */
  actor?: string;
  action?: string;
  target_type?: string;
  target_id?: string;
}

export interface AuditListResult {
  entries: IxvAuditEntry[];
  limit: number;
  offset: number;
  /** The server's audit-action catalog — feeds the action filter dropdown. */
  actions: string[];
}

function auditQuery(filters: AuditFilters): Record<string, string> {
  const query: Record<string, string> = {};
  for (const key of ['from', 'to', 'actor', 'action', 'target_type', 'target_id'] as const) {
    const v = filters[key];
    if (v !== undefined && v !== '') query[key] = v;
  }
  return query;
}

export async function listAudit(
  filters: AuditFilters = {},
  page: { limit?: number; offset?: number } = {}
): Promise<AuditListResult> {
  const query: Record<string, string | number> = auditQuery(filters);
  if (page.limit != null) query.limit = page.limit;
  if (page.offset != null) query.offset = page.offset;
  const { data } = await api.get('/api/ixverify/audit', { params: query });
  return data;
}

/** Server-generated CSV (same filters, capped server-side) as a Blob for a
 *  client-side download — the axios instance carries the auth headers a plain
 *  link cannot. */
export async function exportAuditCsv(filters: AuditFilters = {}): Promise<Blob> {
  const { data } = await api.get('/api/ixverify/audit/export', {
    params: auditQuery(filters),
    responseType: 'blob',
  });
  return data instanceof Blob ? data : new Blob([data], { type: 'text/csv' });
}

/** 403 IXV_ADMIN_REQUIRED — the viewer's role wall. */
export function isAdminRequired(err: unknown): boolean {
  const resp = (err as any)?.response;
  return resp?.status === 403 && resp?.data?.code === 'IXV_ADMIN_REQUIRED';
}

// ---------------------------------------------------------------------------
// Accuracy dashboard metrics (LIVE — WP-5.2). Admin-only server-side (403
// IXV_ADMIN_REQUIRED for anyone else); the §5.5 metrics of record, aggregated
// server-side. PHI-free by construction (R3: scores/counts/percentiles/costs).
// ---------------------------------------------------------------------------

/** A p50/p90 duration summary in milliseconds (the R11 stopwatch). */
export interface DurationSummary {
  n: number;
  p50_ms: number | null;
  p90_ms: number | null;
}

export interface IxvStudyMetric {
  site_study_id: string;
  protocol_id: string | null;
  state: 'untaught' | 'teaching' | 'learned' | string;
  high_scrutiny: boolean;
  confirmed_sessions: number;
  completed_sessions: number;
  self_eval_accuracy: number | null;
  self_eval_sampled: number;
  self_eval_last_run_at: string | null;
  self_eval_field_accuracy: Record<string, number> | null;
  self_eval_history: Array<{ at: string; accuracy: number | null; sampled: number; correction_rate: number | null }>;
  correction_rate: number | null;
  field_correction_rates: Record<string, number>;
}

export interface IxvMetrics {
  window: { from: string; to: string; days: number };
  truncated: boolean;
  summary: {
    total_sessions: number;
    completed_sessions: number;
    verified_sessions: number;
    cancelled_sessions: number;
    mismatch_sessions: number;
    verifications: number;
    adoption: { active_users: number; sessions_per_user_per_week: number; verifications_per_week: number };
    capture_retake_rate: number | null;
    capture_samples: number;
    cost: {
      total_cents: number;
      verification_cents: number;
      self_eval_cents: number;
      per_verification_cents: number | null;
    };
  };
  stopwatch: { live: DurationSummary; staged: DurationSummary; attest_to_publish: DurationSummary };
  studies: IxvStudyMetric[];
  correction_trends: { by_field: Record<string, number | null> };
  high_scrutiny_studies: number;
  scored_fields: string[];
  generated_at: string;
}

export async function getMetrics(params: { days?: number } = {}): Promise<IxvMetrics> {
  const query: Record<string, number> = {};
  if (params.days != null) query.days = params.days;
  const { data } = await api.get('/api/ixverify/metrics', { params: query });
  return data;
}

// ---------------------------------------------------------------------------
// Notifications bell (LIVE — WP-3.4). Rows are PERSONAL (server-scoped to the
// verified JWT identity) and PHI-free by construction (R3).
// ---------------------------------------------------------------------------

export type IxvNotificationType =
  | 'second_check_requested'
  | 'second_check_approved'
  | 'second_check_rejected'
  | 'mismatch_recorded'
  | 'mismatch_reopened'
  | 'report_sent'
  | 'report_delivery_failed'
  | 'roster_sync_anomaly'
  | 'drift_alert';

export interface IxvNotification {
  id: string;
  user_id: string;
  type: IxvNotificationType | string;
  title: string;
  message: string | null;
  session_id: string | null;
  read: boolean;
  created_at: string;
}

export interface NotificationListResult {
  notifications: IxvNotification[];
  unread: number;
  limit: number;
  offset: number;
}

export async function listNotifications(params: { limit?: number; offset?: number; unread?: boolean } = {}): Promise<NotificationListResult> {
  const query: Record<string, string | number> = {};
  if (params.limit != null) query.limit = params.limit;
  if (params.offset != null) query.offset = params.offset;
  if (params.unread) query.unread = 'true';
  const { data } = await api.get('/api/ixverify/notifications', { params: query });
  return data;
}

export async function getUnreadCount(): Promise<number> {
  const { data } = await api.get('/api/ixverify/notifications/unread-count');
  return data?.unread ?? 0;
}

export async function markNotificationRead(id: string): Promise<IxvNotification> {
  const { data } = await api.post(`/api/ixverify/notifications/${id}/read`);
  return data.notification;
}

export async function markAllNotificationsRead(): Promise<number> {
  const { data } = await api.post('/api/ixverify/notifications/read-all');
  return data?.updated ?? 0;
}

/** In-app deep link for a notification — the client mirror of the backend's
 *  ixvNotify.deepLinkPath (keep the two maps in sync). */
export function deepLinkFor(n: Pick<IxvNotification, 'type' | 'session_id'>): string {
  switch (n.type) {
    case 'second_check_approved':
    case 'second_check_rejected':
    case 'second_check_expired':
    case 'mismatch_recorded':
    case 'mismatch_reopened':
    case 'report_sent':
    case 'report_delivery_failed':
      return n.session_id ? `/ixverify/history?session=${n.session_id}` : '/ixverify/history';
    case 'drift_alert':
      return '/ixverify/studies';
    case 'roster_sync_anomaly':
      return '/ixverify/subjects';
    case 'second_check_requested':
      // The row carries only session_id — GET /checks/:id resolves a session
      // id too (checks are UNIQUE per session), so this lands on the review.
      return n.session_id ? `/ixverify/checks/${n.session_id}` : '/ixverify';
    default:
      return '/ixverify';
  }
}

// ---------------------------------------------------------------------------
// Second check + verifier picker (LIVE — WP-4.1). Routes 503
// IXV_SECOND_CHECK_DISABLED while the rollback flag is off (default on).
// ---------------------------------------------------------------------------

export interface SiteUserLite {
  id: string;
  first_name?: string | null;
  last_name?: string | null;
  email?: string | null;
}

export async function listSiteUsers(): Promise<SiteUserLite[]> {
  const { data } = await api.get('/api/ixverify/site-users');
  return Array.isArray(data) ? data : data?.users ?? [];
}

export type SecondCheckStatus = 'requested' | 'approved' | 'rejected' | 'expired';

export interface CheckPersonRef {
  id: string;
  name: string | null;
  email: string | null;
}

/** The raw `ixv_second_checks` row as it rides session detail. */
export interface IxvSecondCheckRow {
  id: string;
  session_id: string;
  status: SecondCheckStatus | string;
  requested_by: string;
  verifier_id: string;
  requested_at: string;
  decided_at: string | null;
  rejection_reason: string | null;
  signature_meaning: string | null;
}

/** The shaped check (enriched identities + computed expiry). */
export interface IxvSecondCheck extends IxvSecondCheckRow {
  expires_at: string | null;
  is_expired?: boolean;
  requester: CheckPersonRef | null;
  verifier: CheckPersonRef | null;
}

export interface CheckEvidenceItem {
  id: string;
  kind: AssetKind | string;
  content_type: string | null;
  page_index?: number | null;
  sha256: string | null;
  /** Presigned inline URL; null when presigning degraded. */
  url: string | null;
}

/** GET /checks/:id — the verifier's read-only review payload (§3.3). */
export interface CheckReviewPayload {
  check: IxvSecondCheck;
  /** The session WITHOUT the extraction proposal (confirmed values only). */
  session: Omit<IxvSession, 'extraction'>;
  kits: IxvKit[];
  kit_state: KitState;
  subject: IxvSubject | null;
  evidence: { documents: CheckEvidenceItem[]; labels: CheckEvidenceItem[] };
  viewer: { is_verifier: boolean; can_decide: boolean };
  /** The exact Part-11 meaning the approve modal must display (the server
   *  refuses any other text). */
  countersign_meaning: string;
}

/** Client mirror of the server's canonical meaning (§3.3 verbatim); the
 *  payload's `countersign_meaning` is authoritative. */
export const COUNTERSIGN_MEANING =
  'I verified the dispensed kit numbers match the IXRS assignment and subject identity';

export async function requestSecondCheck(
  sessionId: string,
  verifierId: string
): Promise<{ check: IxvSecondCheck; session: IxvSessionSummary }> {
  const { data } = await api.post(`/api/ixverify/sessions/${sessionId}/second-check`, { verifier_id: verifierId });
  return data;
}

/** `id` may be a check id or a session id (checks are UNIQUE per session). */
export async function getCheck(id: string): Promise<CheckReviewPayload> {
  const { data } = await api.get(`/api/ixverify/checks/${id}`);
  return data;
}

/** Approve & sign (Part 11): the server re-authenticates the password via
 *  GoTrue; a failed attempt is audited and 401s (IXV_REAUTH_FAILED). */
export async function approveCheck(
  id: string,
  password: string,
  meaning: string = COUNTERSIGN_MEANING
): Promise<{
  check: IxvSecondCheck;
  session: IxvSessionSummary;
  report: { id: string; revision: number; sha256: string; page_count: number };
  distribution: Array<{ to: string; channel: string; status: string }>;
}> {
  const { data } = await api.post(`/api/ixverify/checks/${id}/approve`, { password, meaning });
  return data;
}

export async function rejectCheck(
  id: string,
  reason: string
): Promise<{ check: IxvSecondCheck; session: IxvSessionSummary }> {
  const { data } = await api.post(`/api/ixverify/checks/${id}/reject`, { reason });
  return data;
}

/** 401 IXV_REAUTH_FAILED — wrong password on the Part-11 re-auth (the
 *  attempt is audited server-side). */
export function isReauthFailed(err: unknown): boolean {
  const resp = (err as any)?.response;
  return resp?.status === 401 && resp?.data?.code === 'IXV_REAUTH_FAILED';
}

/** 503 IXV_SECOND_CHECK_DISABLED — the WP-4.1 rollback flag is off. */
export function isSecondCheckDisabled(err: unknown): boolean {
  const resp = (err as any)?.response;
  return resp?.status === 503 && resp?.data?.code === 'IXV_SECOND_CHECK_DISABLED';
}

// --- More error helpers (session flow) -------------------------------------

/** 503 `IXVERIFY_DISABLED` — the R2 kill switch. The tool is dark; surface a
 *  calm "currently disabled" state, never an error loop. */
export function isKillSwitched(err: unknown): boolean {
  const resp = (err as any)?.response;
  return resp?.status === 503 && resp?.data?.code === 'IXVERIFY_DISABLED';
}

/** The machine-readable IXV_* code on a session-flow error response, if any. */
export function errorCode(err: unknown): string | null {
  return (err as any)?.response?.data?.code ?? null;
}

/** Barcode+vision disagreement payload (409 IXV_SOURCES_DISAGREE) — both
 *  readings for the required human tap (`crossCheckKitNumbers` shapes). */
export function sourcesDisagreement(err: unknown): { barcode: string[]; vision: string[] } | null {
  const data = (err as any)?.response?.data;
  if (data?.code !== 'IXV_SOURCES_DISAGREE') return null;
  const cross = data.crossCheck || {};
  if (Array.isArray(cross.confusablePairs) && cross.confusablePairs.length > 0) {
    return {
      barcode: cross.confusablePairs.map((p: any) => p.barcodeValue).filter(Boolean),
      vision: cross.confusablePairs.map((p: any) => p.visionValue).filter(Boolean),
    };
  }
  return {
    barcode: cross.barcodeValues || [],
    vision: cross.visionValues || [],
  };
}
